import BreadCrum from "../basic/BreadCrum";
import { HeadingTitle, Paragraph } from "../basic/title";
import { AccountSideNav } from "../myaccount/sidenav";
import { MdDeleteForever } from "react-icons/md";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import {
  fetchUserWishlists,
  removeFromWishlist,
  removeFromListApi,
  updateWishlistQuantity,
} from "../../../redux/slices/wishlist";
import { addCartItems } from "../../../redux/slices/addToCart";
import { useNavigate } from "react-router-dom";

const BASE_IMAGE_URL = process.env.REACT_APP_BASE_IMAGE_URL;
function WishlistGroupedPage() {
  const [wishlistLists, setWishlistLists] = useState({});
  const [selectedItems, setSelectedItems] = useState({});
  const [masterCheck, setMasterCheck] = useState({});
  const [showAll, setShowAll] = useState(false);
  const [openListName, setOpenListName] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const user_id = user?.id;

  useEffect(() => {
    if (!user_id) return;

    const loadWishlists = async () => {
      const response = await dispatch(fetchUserWishlists(user_id, toast));
      if (response?.success && response.data) {
        const wishlistObj = {};
        response.data.forEach((list) => {
          wishlistObj[list.name || "Default"] = list;
        });

        setWishlistLists(wishlistObj);

        const allListNames = Object.keys(wishlistObj);
        if (allListNames.length > 0) {
          const fallbackList = allListNames.includes(openListName)
            ? openListName
            : allListNames[allListNames.length - 1];
          setOpenListName(fallbackList);
        }

        localStorage.setItem(
          `wishlists_${user_id}`,
          JSON.stringify(wishlistObj)
        );

        const allItems = Object.values(wishlistObj).flatMap(
          (list) => list.items || []
        );
        localStorage.setItem(
          `wishlist_items_${user_id}`,
          JSON.stringify(allItems)
        );
      } else {
        toast.error("Failed to load wishlists.");
      }
    };

    loadWishlists();
  }, [user_id, openListName]);

  const handleMasterCheckbox = (listName) => {
    const checked = !masterCheck[listName];
    setMasterCheck((prev) => ({ ...prev, [listName]: checked }));

    const newSelected = {};
    wishlistLists[listName]?.items?.forEach((item) => {
      const key = getKey(item, listName);
      newSelected[key] = checked;
    });

    setSelectedItems((prev) => ({ ...prev, ...newSelected }));
  };

  const handleItemCheckbox = (key) => {
    setSelectedItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const removeProductFromList = async (listName, productId, selectedVariants) => {
    const list = wishlistLists[listName]?.items || [];
    const itemToRemove = list.find(
      (item) =>
        item.product_id === productId &&
        JSON.stringify(item.selected_variants || {}) === JSON.stringify(selectedVariants || {})
    );

    if (!itemToRemove?.id) {
      toast.error("Wishlist item ID not found");
      return;
    }

    const result = await removeFromWishlist(itemToRemove.id, toast)();
    if (!result) return;

    const updatedList = list.filter((item) => item.id !== itemToRemove.id);
    const updatedWishlistLists = { ...wishlistLists };

    if (updatedList.length === 0 && listName !== "Default") {
      delete updatedWishlistLists[listName];
      toast.success(`All products removed, so "${listName}" list deleted.`);
    } else {
      updatedWishlistLists[listName] = {
        ...updatedWishlistLists[listName],
        items: updatedList,
      };
      toast.success("Product removed from list.");
    }

    setWishlistLists(updatedWishlistLists);

    const key = getKey({ product_id: productId, selected_variants: selectedVariants }, listName);
    setSelectedItems((prev) => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  };

  const removeList = async (listName) => {
    if (listName === "Default") {
      toast.warn("Default list cannot be removed.");
      return;
    }

    const listObj = wishlistLists[listName];
    const wishlistId = listObj?.id;

    if (!wishlistId) {
      toast.error("Wishlist ID missing.");
      return;
    }

    const result = await removeFromListApi(wishlistId, toast)();
    if (!result?.success) return;

    const updated = { ...wishlistLists };
    delete updated[listName];

    const remainingLists = Object.keys(updated);
    setOpenListName(remainingLists[remainingLists.length - 1] || "");

    setWishlistLists(updated);
    toast.success(`Removed list: ${listName}`);
  };

  const getKey = (item, listName) => {
    const variantString = Object.entries(item?.selected_variants || {})
      .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
      .map(([key, value]) => `${key}:${value?.id || value?.value || 'default'}`)
      .join("-");
    return `${listName}-${item.product_id}-${variantString}`;
  };

  const handleAddSelectedToCart = async () => {
    const selected = Object.entries(selectedItems)
      .filter(([_, isSelected]) => isSelected)
      .map(([key]) => {
        const [listName, product_id, ...variantParts] = key.split("-");
        const selected_variants = {};
        variantParts.forEach((part) => {
          const [attr, val] = part.split(":");
          selected_variants[attr] = { value: val };
        });
  
        return {
          product_id: parseInt(product_id),
          listName,
          selected_variants,
        };
      });
  
    if (selected.length === 0) {
      toast.warning("Please select at least one item to add to cart.");
      return;
    }
  
    const cart_id = localStorage.getItem("cart_id");
    const customer_id = JSON.parse(localStorage.getItem("user"))?.id;
  
    for (const item of selected) {
      const list = wishlistLists[item.listName]?.items || [];
  
      const listItem = list.find((x) => {
        const xVariants = x.selected_variants || {};
        const selVariants = item.selected_variants || {};
  
        if (x.product_id !== item.product_id) return false;
        if (Object.keys(selVariants).length !== Object.keys(xVariants).length) return false;
  
        return Object.keys(selVariants).every(
          (attr) => xVariants[attr]?.value === selVariants[attr]?.value
        );
      });
  
      if (!listItem) {
        toast.error(`Product with ID ${item.product_id} not found in ${item.listName} list.`);
        continue;
      }
  
      const product = listItem.product;
      const rawVariants = listItem.selected_variants || {};
      const variantId = listItem.variant_id;
  
      let isOutOfStock = false;
      const outOfStockDetails = [];
  
      Object.values(rawVariants).forEach((variant) => {
        if (
          variant.stock_status?.toLowerCase() === "out_of_stock" ||
          Number(variant.stock_quantity) === 0
        ) {
          isOutOfStock = true;
          outOfStockDetails.push(variant.value || "variant");
        }
      });
  
      if (isOutOfStock) {
        toast.warning(
          `${product?.name || "Product"} (${outOfStockDetails.join(", ")}) is out of stock.`
        );
        continue;
      }
  
      const selectedVariantsToSend = {};
      Object.entries(rawVariants).forEach(([key, variant]) => {
        selectedVariantsToSend[key] = {
          attribute: key,
          value: variant.value || "",
        };
      });
  
      const cartItem = {
        item_id: listItem.product_id,
        variant_id: variantId,
        quantity: listItem.quantity || 1,
        selectedVariants: selectedVariantsToSend,
        price: parseFloat(listItem.price || 0),
        discount_price: parseFloat(listItem.discount_price || listItem.price || 0),
        ...(cart_id && { cart_id }),
        ...(customer_id && { customer_id }),
      };
  
      dispatch(addCartItems(cartItem, toast, navigate, false));
      toast.success("Selected items added to cart!");
    }
  };

  const handleQuantityChange = async (itemId, newQty, listName) => {
    const updatedLists = { ...wishlistLists };
    const item = updatedLists[listName]?.items?.find((item) => item.id === itemId);
  
    if (!item) return;
  
    const rawVariants = item.selected_variants || {};
    let maxQty = item.product?.stock_quantity || 1;
  
    const variantStockList = Object.values(rawVariants).map((v) => Number(v.stock_quantity || 0));
    if (variantStockList.length) {
      maxQty = Math.min(...variantStockList);
    }
  
    if (newQty > maxQty) {
      toast.warning(`Only ${maxQty} item(s) available in stock.`);
      newQty = maxQty;
    }
  
    if (newQty < 1) newQty = 1;
  
    // Wait for dispatch to finish
    const res = await dispatch(updateWishlistQuantity(itemId, newQty, toast));
  
    if (res?.success) {
      updatedLists[listName].items = updatedLists[listName].items.map((itm) => {
        if (itm.id === itemId) {
          return { ...itm, quantity: newQty };
        }
        return itm;
      });
      setWishlistLists(updatedLists);
    }
  };


  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="container mx-auto max-w-7xl px-4 py-6">
      <BreadCrum
        componentName="My Account"
        link="/my-account"
        componentSecondName="Wishlist"
      />
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-4">
          <AccountSideNav />
        </div>
        <div className="col-span-12 lg:col-span-8">
          <div className="border-2 border-[#00A762] p-6 rounded-2xl shadow bg-white">
            <div className="flex justify-between items-center mb-6">
              <HeadingTitle title="My Wishlist Lists" textAlign="left" />
              {Object.keys(wishlistLists).length > 1 && (
                <button
                  className="bg-[#00A762] text-white px-4 py-1.5 rounded-md text-sm font-medium shadow hover:bg-green-700 transition"
                  onClick={() => setShowAll(!showAll)}
                >
                  {showAll ? "Hide All Lists" : "Show All Lists"}
                </button>
              )}
            </div>

            {Object.entries(wishlistLists).map(([listName, listObj]) => {
              if (!showAll && listName !== openListName) return null;

              return (
                <div
                  key={listName}
                  className="mb-6 border border-gray-200 rounded-xl bg-gray-50 p-4 shadow-sm"
                >
                  <div
                    className="flex justify-between items-center mb-4 cursor-pointer"
                    onClick={() => setOpenListName(listName)}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={masterCheck[listName] || false}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleMasterCheckbox(listName);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 accent-green-600"
                      />
                      <h2 className="text-lg font-semibold text-[#4aaf85] capitalize">
                        {listName}
                      </h2>
                    </div>

                    {listName !== "Default" && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeList(listName);
                        }}
                        className="text-red-600 hover:text-red-800 flex items-center gap-1 text-sm"
                      >
                        <MdDeleteForever className="text-lg" /> Remove List
                      </button>
                    )}
                  </div>

                  {openListName === listName && (
                    <div>
                      {(listObj.items || []).map((item) => {
                        const selectedVariants = item.selected_variants || {};
                        const key = getKey(item, listName);

                        let isOutOfStock = false;
                        let variantDisplay = [];

                        if (Object.keys(selectedVariants).length > 0) {
                          Object.entries(selectedVariants).forEach(([attr, variant]) => {
                            if (variant.value) {
                              variantDisplay.push(variant.value); // Only push the value, not "attribute: value"
                            }
                            if (variant.stock_status === "out_of_stock" || Number(variant.stock_quantity) === 0) {
                              isOutOfStock = true;
                            }
                          });
                        } else {
                            if (item.product.stock_status === "out_of_stock" || Number(item.product.stock_quantity) === 0) {
                                isOutOfStock = true;
                            }
                        }


                        return (
                          <div
                            key={key}
                            className="flex items-center justify-between py-4 border-b last:border-b-0"
                          >
                            <div className="flex items-center gap-4">
                              <input
                                type="checkbox"
                                checked={!!selectedItems[key]}
                                onChange={() => handleItemCheckbox(key)}
                                className="w-4 h-4 accent-green-600"
                              />
                              <img
                                src={`${BASE_IMAGE_URL}${item?.product?.image}`}
                                alt="product"
                                className="w-12 h-12 rounded object-cover border"
                              />

                              <div>
                                <h3 className="text-base font-semibold text-gray-800 capitalize">
                                  {item?.product?.name || "Product Name"}
                                </h3>
                                <Paragraph
                                  title={`Added on: ${formatDate(
                                    item?.created_at
                                  )}`}
                                />
                                {isOutOfStock && (
                                  <p className="text-xs text-red-600 font-medium">Out of stock</p>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-2 mt-1">
                              <label className="text-sm text-gray-600">Qty:</label>
                              <input
                                type="number"
                                className="border px-2 py-1 rounded w-16"
                                value={item.quantity || 1}
                                min={1}
                                onChange={(e) =>
                                  handleQuantityChange(
                                    item.id,
                                    parseInt(e.target.value),
                                    listName,
                                    item.product_id,
                                    selectedVariants
                                  )
                                }
                              />
                            </div>
                            <div className="text-right space-y-1">
                              {variantDisplay.length > 0 && (
                                <p className="text-sm text-gray-600">
                                  {variantDisplay.join(", ")}
                                </p>
                              )}
                              <p className="text-green-600 font-bold">
                                ₹{item.discount_price || item.price}
                              </p>
                              <button
                                onClick={() =>
                                  removeProductFromList(
                                    listName,
                                    item.product_id,
                                    selectedVariants
                                  )
                                }
                                className="text-xs text-red-600 hover:underline"
                              >
                                Remove Product
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {Object.keys(wishlistLists).length === 0 && (
              <p className="text-center text-gray-500 mt-4">
                No wishlist lists found.
              </p>
            )}

            {Object.values(selectedItems).some((selected) => selected) && (
              <div className="text-right mt-6">
                <button
                  className="bg-[#00A762] text-white px-6 py-2 rounded-lg shadow-md hover:bg-green-700 transition"
                  onClick={handleAddSelectedToCart}
                >
                  Add Selected to Cart
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
export default WishlistGroupedPage;